/**
 * Shared time helpers for values stored as `timestamptz` (absolute instants).
 *
 * Never slice the hour/minute out of a timestamp string: the text that comes
 * back from Postgres is in the connection's timezone (UTC), not the viewer's.
 * Parse the instant and format it in local time instead.
 */

/** `HH:MM` in the viewer's local timezone. Accepts an ISO timestamp or a bare `HH:MM(:SS)`. */
export function localHHMM(value: string | null | undefined): string | null {
  if (!value) return null;
  // Legacy plain time-of-day values ("23:00" / "23:00:00") have no instant to convert.
  const bare = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value);
  if (bare) return `${bare[1]}:${bare[2]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Absolute instant (ISO, UTC) for a local calendar date + wall-clock time.
 * `new Date(y, m, d, hh, mm)` applies the runtime's real UTC offset, so a
 * 23:00 entry in UTC+2 is stored as 21:00Z rather than a naive 23:00Z.
 */
export function localDateTimeToISO(dateISO: string, hhmm: string): string | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateISO);
  const tm = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!dm || !tm) return null;
  const d = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2]), 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Same as `localDateTimeToISO`, but shifted `days` local calendar days. */
export function localDateTimeToISOShifted(dateISO: string, hhmm: string, days: number): string | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateISO);
  const tm = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!dm || !tm) return null;
  const d = new Date(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]) + days, Number(tm[1]), Number(tm[2]), 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
